/**
 * This file has the message formats for the different messages that are
 * received by the client from the websocket.
 *
 * Each websocket `message` contains an `event` that has a:
 *  - `type`,
 *  - `parent_type` and
 *  - `data`.
 *
 * These `events` are then reused by the `store` and `actions`.
 */
import {
  WebsocketMessagesEvent,
  WebsocketNotificationsEvent,
  WebsocketOnlineOfflineEvent,
} from '.';
import { TimestampSeconds } from '../time/time.model';

export type WebsocketServerEvent = SubMessageEvent | WebsocketMessage;

interface SubMessageEvent {
  readonly channel: 'subscribe';
  readonly body: 'OK';
}

export type BaseServerData = TimestampSeconds & {
  readonly id: string;
  readonly type: string;
  readonly parent_type: string;
};

export type WebsocketEvent =
  | WebsocketNotificationsEvent
  | WebsocketOnlineOfflineEvent
  | WebsocketMessagesEvent;

export function isWebsocketMessage(
  event: WebsocketServerEvent,
): event is WebsocketMessage {
  return event.channel === 'user';
}

type BaseMessage = BaseServerData & {
  readonly channel: 'user';
  readonly body: {
    readonly aggregated: null;
    readonly display: boolean;
    readonly id: string;
    readonly is_important: boolean;
    readonly is_notification: boolean;
    readonly is_project: boolean;
    readonly message: string;
    readonly news_feed: boolean;
    readonly no_persist: boolean;
    readonly popup: boolean;
    readonly targets: ReadonlyArray<any>;
    readonly timestamp: number;
  };
};

export type WebsocketMessage = BaseMessage & {
  readonly body: BaseMessage['body'] & WebsocketEvent;
};
